import { FC, memo, useState } from 'react';
import Blocks from '../Blocks/Blocks';
import SiteMenu from '../SiteMenu/SiteMenu';
import { OutgoingCalls } from './OutgoingCalls/OutgoingCalls';
import { OutgoingCallsFilter } from './OutgoingCalls/OutgoingCallsFilter/OutgoingCallsFilter';
import { useOutgoingCalls } from '../../hooks/components/useOutgoingCalls/useOutgoingCalls';
import { CallsTop } from './CallsTop/CallsTop';
import { RightFilterPanel } from '../shared/RightFilterPanel/RightFilterPanel';
import { TaskCampaignFilter } from '../shared/TaskCampaignFilter';
import blockStyles from '../Blocks/style.module.css';
import { useOutgoingReport } from '../../hooks/components/useOutgoingReport/useOutgoingReport';
import { OutgoingReportTypeModal } from '../../features/reports/OutgoingReportTypeModal/OutgoingReportTypeModal';

interface Props {
  selectedKey: string;
  updateData: (key: string) => void;
  showIncoming: boolean;
}

export const Calls: FC<Props> = memo(
  ({ selectedKey, updateData, showIncoming }: Props) => {
    const propsOutgoingCalls = useOutgoingCalls();
    const outgoingReport = useOutgoingReport(
      propsOutgoingCalls.callRequest,
      propsOutgoingCalls.handleOkReport,
    );
    const [isFiltersOpen, setIsFiltersOpen] = useState(false);

    const handleToggleFilters = () => {
      setIsFiltersOpen((prev) => !prev);
    };

    return (
      <>
        <Blocks
          topBlock={
            <div className={blockStyles.topBlockGrid}>
              <SiteMenu
                title="Журнал исходящих заданий"
                svg={true}
                svgType="outgoingCalls"
              />

              <CallsTop
                onSearch={propsOutgoingCalls.handleSearch}
                onRangePicker={propsOutgoingCalls.handleRangePicker}
                dateRange={propsOutgoingCalls.dateRange}
                onOkReport={outgoingReport.handleReportClick}
                onToggleFilters={handleToggleFilters}
                isFiltersOpen={isFiltersOpen}
                isReportLoading={outgoingReport.isReportLoading}
                activeFilterCount={propsOutgoingCalls.activeFilterCount}
                disabled={propsOutgoingCalls.isFilterLoading}
              />
            </div>
          }
          mainBlock={
            <OutgoingCalls
              key="outgoing"
              pageSize={propsOutgoingCalls.pageSize}
              totalCount={propsOutgoingCalls.totalCount}
              onChange={propsOutgoingCalls.handleChange}
              callRequest={propsOutgoingCalls.callRequest}
              total={propsOutgoingCalls.total}
              allCalls={propsOutgoingCalls.allCalls}
              COLUMNS={propsOutgoingCalls.COLUMNS}
              onTableChange={propsOutgoingCalls.onTableChange}
              divider={propsOutgoingCalls.divider}
              handlePageSizeChange={propsOutgoingCalls.handlePageSizeChange}
              filtersCollapsed={!isFiltersOpen}
              scenarioLabelByType={propsOutgoingCalls.scenarioLabelByType}
              resultLabelByValue={propsOutgoingCalls.resultLabelByValue}
              loading={propsOutgoingCalls.isTableLoading}
              hasLoadError={propsOutgoingCalls.hasLoadError}
            />
          }
          rightBlockOpen={isFiltersOpen}
          reserveRightSpace={true}
          rightBlockWidth={300}
          rightBlock={
            <RightFilterPanel
              onReset={propsOutgoingCalls.resetFilters}
              onClose={handleToggleFilters}
              resetDisabled={
                propsOutgoingCalls.isResetDisabled ||
                propsOutgoingCalls.isFilterLoading
              }
            >
              <OutgoingCallsFilter
                campaignFilter={
                  <TaskCampaignFilter
                    {...propsOutgoingCalls.campaignFilter}
                    disabled={propsOutgoingCalls.isFilterLoading}
                  />
                }
                clinics={propsOutgoingCalls.clinics}
                specializations={propsOutgoingCalls.specializations}
                surnameDoctors={propsOutgoingCalls.surnameDoctors}
                onSearch={propsOutgoingCalls.handleSearch}
                filterOption={propsOutgoingCalls.filterOption}
                onClinicsChange={propsOutgoingCalls.handleClinicsChange}
                clinicValue={propsOutgoingCalls.clinicValue}
                resultValue={propsOutgoingCalls.resultValue}
                onResultChange={propsOutgoingCalls.handleResultChange}
                taskStatusValue={propsOutgoingCalls.taskStatusValue}
                onTaskStatusChange={propsOutgoingCalls.handleTaskStatusChange}
                attemptStatusValue={propsOutgoingCalls.attemptStatusValue}
                onAttemptStatusChange={
                  propsOutgoingCalls.handleAttemptStatusChange
                }
                specializationValue={propsOutgoingCalls.specializationValue}
                onSpecializationChange={
                  propsOutgoingCalls.handleSpecializationChange
                }
                doctorsValue={propsOutgoingCalls.doctorsValue}
                onDoctorsChange={propsOutgoingCalls.handleDoctorsChange}
                onOpenClinics={propsOutgoingCalls.handleOpenClinics}
                onOpenResult={propsOutgoingCalls.handleOpenResult}
                onOpenTaskStatus={propsOutgoingCalls.handleOpenTaskStatus}
                onOpenAttemptStatus={propsOutgoingCalls.handleOpenAttemptStatus}
                onOpenSpecialization={
                  propsOutgoingCalls.handleOpenSpecialization
                }
                onOpenDoctors={propsOutgoingCalls.handleOpenDoctors}
                notificationTypeValue={propsOutgoingCalls.notificationTypeValue}
                onNotificationTypeChange={
                  propsOutgoingCalls.handleNotificationTypeChange
                }
                onOpenNotificationType={
                  propsOutgoingCalls.handleOpenNotificationType
                }
                attemptTimeRangeValue={propsOutgoingCalls.attemptTimeRangeValue}
                onAttemptTimeRangeChange={
                  propsOutgoingCalls.handleAttemptTimeRangeChange
                }
                taskStatusOptions={propsOutgoingCalls.taskStatusOptions}
                resultOptions={propsOutgoingCalls.resultOptions}
                scenarioOptions={propsOutgoingCalls.scenarioOptions}
                disabled={propsOutgoingCalls.isFilterLoading}
              />
            </RightFilterPanel>
          }
        />
        <OutgoingReportTypeModal
          open={outgoingReport.isModalOpen}
          loading={outgoingReport.isReportLoading}
          onCancel={outgoingReport.handleCancel}
          onGenerate={outgoingReport.handleGenerate}
        />
      </>
    );
  },
);

Calls.displayName = 'Calls';
